import { useCurrency, Money, GbpNote } from "../lib/currency";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Wallet, ShoppingBag, TrendingUp, ArrowRight, Compass, DollarSign, Megaphone, Plus } from "lucide-react";
import PushBell from "../components/PushBell";
import { BrandVerifyBanner } from "../components/BrandVerifyBanner";

function StatusPill({ status }) {
  const map = {
    pending: "bg-zinc-500/10 text-zinc-300 border-zinc-500/30",
    in_progress: "bg-nexus-blue/10 text-nexus-blue border-nexus-blue/30",
    delivered: "bg-yellow-500/10 text-yellow-300 border-yellow-500/30",
    released: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    disputed: "bg-nexus-pink/10 text-nexus-pink border-nexus-pink/30",
  };
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${map[status] || map.pending}`}>
      {status.replace("_", " ")}
    </span>
  );
}

const TIER_LABEL = { free: "Free", starter: "Starter", pro: "Pro" };

export default function BrandDashboard() {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const [hires, setHires] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/auth?mode=login"); return; }
    if (user.role !== "brand") { navigate("/creator"); return; }
    api.get("/hires").then((r) => setHires(r.data));
    api.get("/campaigns", { params: { mine: true, status: "all" } }).then((r) => setCampaigns(r.data)).catch(() => {});
  }, [user, navigate, loading]);

  if (!user) return null;

  const spend = hires.filter((h) => h.status === "released").reduce((s, h) => s + (h.brand_total || 0), 0);
  const active = hires.filter((h) => ["in_progress", "delivered"].includes(h.status));

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 space-y-8">
        <BrandVerifyBanner />
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Brand dashboard</div>
            <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tighter">
              Welcome, <span className="nexus-gradient-text">{profile?.company || user.name}</span>.
            </h1>
          </div>
          <div className="flex gap-3">
            <Link to="/campaigns/new">
              <Button variant="outline" className="bg-white/5 border-white/15 text-white hover:bg-white/10 hover:text-white rounded-full" data-testid="new-campaign-btn">
                <Megaphone className="h-4 w-4 mr-2" /> Post campaign
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="outline" className="bg-white/5 border-white/15 text-white hover:bg-white/10 hover:text-white rounded-full" data-testid="upgrade-btn">
                <DollarSign className="h-4 w-4 mr-2" />
                {profile?.subscription_tier === "pro" ? "Pro plan" : "Upgrade"}
              </Button>
            </Link>
            <Link to="/discover">
              <Button className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90" data-testid="discover-cta-btn">
                <Compass className="h-4 w-4 mr-2" /> Discover creators
              </Button>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-5 rounded-2xl bg-[#0C0C10] hairline card-hover">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Total spend</div>
              <Wallet className="h-4 w-4 text-nexus-blue" />
            </div>
            <div className="font-display font-black text-3xl nexus-gradient-text"><Money gbp={spend} /></div>
          </div>
          <div className="p-5 rounded-2xl bg-[#0C0C10] hairline card-hover">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Active hires</div>
              <ShoppingBag className="h-4 w-4 text-nexus-pink" />
            </div>
            <div className="font-display font-black text-3xl">{active.length}</div>
          </div>
          <div className="p-5 rounded-2xl bg-[#0C0C10] hairline card-hover">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Total hires</div>
              <TrendingUp className="h-4 w-4 text-nexus-purple" />
            </div>
            <div className="font-display font-black text-3xl">{hires.length}</div>
          </div>
          <div className="p-5 rounded-2xl bg-[#0C0C10] hairline card-hover">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Plan</div>
              <DollarSign className="h-4 w-4 text-zinc-400" />
            </div>
            <div className="font-display font-black text-3xl">{TIER_LABEL[profile?.subscription_tier || "free"]}</div>
            <div className="text-xs text-zinc-500 mt-1">
              {{ pro: "10% platform fee", founding: "10% platform fee", starter: "15% platform fee" }[profile?.subscription_tier] || "20% platform fee"}
            </div>
          </div>
        </div>

        <PushBell />

        {campaigns.length > 0 && (
          <section>
            <div className="flex items-baseline justify-between mb-4">
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Your campaigns</div>
              <Link to="/campaigns/new" className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white flex items-center gap-1">
                <Plus className="h-3 w-3" /> New
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="brand-campaigns-list">
              {campaigns.slice(0, 6).map((c) => (
                <Link
                  key={c.id}
                  to={`/campaigns/${c.id}`}
                  className="p-4 rounded-2xl bg-[#0C0C10] hairline card-hover"
                  data-testid={`my-campaign-${c.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                      c.status === "open" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" :
                      c.status === "filled" ? "bg-nexus-purple/10 text-nexus-purple border-nexus-purple/30" :
                      "bg-zinc-500/10 text-zinc-300 border-zinc-500/30"
                    }`}>
                      {c.status}
                    </span>
                    <span className="text-xs text-zinc-500">{c.applications_count || 0} apps</span>
                  </div>
                  <div className="font-semibold truncate">{c.title}</div>
                  <div className="font-display font-bold text-2xl nexus-gradient-text mt-2"><Money gbp={c.budget} decimals={0} /></div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">Your hires</div>
          <div className="space-y-3" data-testid="brand-hires-list">
            {hires.length === 0 ? (
              <div className="glass rounded-2xl p-10 text-center">
                <div className="font-display text-2xl mb-2">No hires yet</div>
                <p className="text-zinc-400 text-sm mb-6">Browse creators and start your first escrow-protected collab.</p>
                <Link to="/discover">
                  <Button className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90">
                    Discover creators <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </div>
            ) : hires.map((h) => (
              <Link
                key={h.id}
                to={`/deals/${h.id}`}
                className="block p-5 rounded-2xl bg-[#0C0C10] hairline card-hover"
                data-testid={`brand-hire-row-${h.id}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <StatusPill status={h.status} />
                      <div className="font-semibold">{h.package?.title || "Package"}</div>
                    </div>
                    <div className="text-sm text-zinc-400">with {h.counterparty_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display font-bold text-xl"><Money gbp={h.brand_total || 0} /></div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total (incl. fee)</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-zinc-500" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
